/**
 * URL sanitisers used as defence-in-depth at link-rendering boundaries.
 * Editors may bypass Sanity's validators (legacy data, paste errors) so
 * any CMS-provided URL or email is re-validated here before being put on
 * an anchor's `href`. Anything that doesn't parse cleanly returns null,
 * letting callers skip rendering rather than emit an unsafe link.
 */

/**
 * Lightweight email shape check. Mirrors the regex used by the Sanity
 * `email` field validator. Intentionally permissive — we're not trying
 * to parse RFC 5322, just block obviously malformed values before
 * constructing a `mailto:`.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validate that a value parses as a URL and uses an http(s) scheme
 * before passing it to an anchor's `href`. Defends against unsafe
 * schemes (`javascript:`, `data:`, etc.) and malformed values that
 * survive Sanity's editor checks. Returns null when the value can't be
 * parsed or the protocol isn't allowed.
 */
export function toSafeHttpUrl(value: string | null): string | null {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:"
      ? parsed.toString()
      : null;
  } catch {
    return null;
  }
}

/**
 * Build a `mailto:` href from a CMS-provided email, returning null when
 * the value doesn't match the expected shape. Keeps parity with
 * `toSafeHttpUrl`'s defensive contract.
 */
export function toSafeMailtoUrl(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  return EMAIL_PATTERN.test(trimmed) ? `mailto:${trimmed}` : null;
}
